/**
 * Wan VACE Management Card
 *
 * Dedicated card for the Additional Installations page that exposes
 * Wan VACE's multi-model + LoRA management capabilities, which the generic
 * ComponentCard cannot represent.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '../../hooks/useTranslation';
import { useHardwareCapabilities } from '../../hooks/useHardwareCapabilities';
import { useWanVace } from '../../hooks/useWanVace';
import {
  installWanVaceLora,
  listWanVaceLoras,
  uninstallWanVaceLora,
  type WanVaceLoRA,
} from '../../services/wanVaceService';

const MODEL_ORDER: string[] = ['vace_1_3b', 'vace_14b_gguf', 'vace_14b_full'];

const VideoIcon: React.FC = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

export const WanVaceCard: React.FC = () => {
  const { t } = useTranslation();
  const { hardware } = useHardwareCapabilities();
  const {
    status,
    loading,
    error,
    installing,
    progress,
    install,
    uninstallModel,
    selectModel,
    dismissError,
    refresh,
  } = useWanVace();

  const [loras, setLoras] = useState<WanVaceLoRA[]>([]);
  const [lorasLoading, setLorasLoading] = useState(false);
  const [lorasError, setLorasError] = useState<string | null>(null);
  const [loraActionId, setLoraActionId] = useState<string | null>(null);

  const vramGb = hardware?.gpu?.vramGb ?? 0;

  const fetchLoras = useCallback(async () => {
    setLorasLoading(true);
    setLorasError(null);
    try {
      const items = await listWanVaceLoras();
      setLoras(items);
    } catch (err) {
      setLorasError(err instanceof Error ? err.message : 'Failed to load LoRAs');
    } finally {
      setLorasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status?.installed) {
      void fetchLoras();
    } else {
      setLoras([]);
    }
  }, [status?.installed, fetchLoras]);

  const handleLoraInstall = useCallback(
    async (loraId: string) => {
      setLoraActionId(loraId);
      setLorasError(null);
      try {
        await installWanVaceLora(loraId);
        await fetchLoras();
        await refresh();
      } catch (err) {
        setLorasError(err instanceof Error ? err.message : 'Failed to install LoRA');
      } finally {
        setLoraActionId(null);
      }
    },
    [fetchLoras, refresh]
  );

  const handleLoraUninstall = useCallback(
    async (loraId: string) => {
      setLoraActionId(loraId);
      setLorasError(null);
      try {
        await uninstallWanVaceLora(loraId);
        await fetchLoras();
        await refresh();
      } catch (err) {
        setLorasError(err instanceof Error ? err.message : 'Failed to uninstall LoRA');
      } finally {
        setLoraActionId(null);
      }
    },
    [fetchLoras, refresh]
  );

  const models = MODEL_ORDER.map((modelId) => {
    const displayName = status?.availableModels[modelId] ?? modelId;
    const reqs = status?.requirements[modelId];
    const vramRequired = reqs?.vramGb ?? 0;
    const diskRequired = reqs?.diskGb ?? 0;
    const isInstalled = !!status?.installedModels.includes(modelId);
    const isSelected = status?.selectedModel === modelId;
    const isCompatible = vramGb >= vramRequired;
    const isActive = isInstalled && isSelected;
    return {
      modelId,
      displayName,
      vramRequired,
      diskRequired,
      isInstalled,
      isSelected,
      isActive,
      isCompatible,
    };
  });

  const anyCompatible = models.some((m) => m.isCompatible);

  if (loading && !status) {
    return (
      <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 bg-white dark:bg-secondary-800">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary-200 dark:bg-secondary-700 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-1/3 mb-2" />
            <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 bg-white dark:bg-secondary-800">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
            <VideoIcon />
          </div>
          <div>
            <h3 className="font-semibold text-secondary-900 dark:text-secondary-100">
              {t('wanVace.title', 'Wan VACE')}
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {t(
                'wanVace.description',
                'Local AI video generation and editing. Text-to-video, image-to-video, inpainting, and pose transfer.'
              )}
            </p>
          </div>
        </div>
        {status?.installed && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            {t('wanVace.status.installed', 'Installed')}
          </span>
        )}
      </div>

      {/* Hardware summary */}
      <div className="flex flex-wrap gap-4 text-sm text-secondary-600 dark:text-secondary-400 mb-3">
        <span>
          {t('wanVace.gpu', 'GPU')}: {hardware?.gpu?.name ?? t('wanVace.noGpu', 'No GPU detected')}
        </span>
        <span>
          {t('wanVace.vramAvailable', 'VRAM available')}: {vramGb.toFixed(1)} GB
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-3 p-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-start justify-between gap-2">
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          <button
            onClick={dismissError}
            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
            aria-label={t('wanVace.dismiss', 'Dismiss')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Install progress */}
      {progress && installing && (
        <div className="mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-secondary-600 dark:text-secondary-400">
              {progress.message || progress.status}
            </span>
            <span className="text-secondary-600 dark:text-secondary-400">
              {Math.round(progress.progress)}%
            </span>
          </div>
          <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.max(0, Math.min(100, progress.progress))}%` }}
            />
          </div>
        </div>
      )}

      {/* Models list */}
      <div className="space-y-2 mb-3">
        <h4 className="label-emphasized">{t('wanVace.availableModels', 'Available models')}</h4>
        {models.map((m) => (
          <div
            key={m.modelId}
            className={`border rounded-lg p-3 ${
              m.isActive
                ? 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                : 'border-secondary-200 dark:border-secondary-700'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-secondary-900 dark:text-secondary-100">
                    {m.displayName}
                  </span>
                  {m.isInstalled && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300">
                      {t('wanVace.installed', 'Installed')}
                    </span>
                  )}
                  {m.isActive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                      {t('wanVace.active', 'Active')}
                    </span>
                  )}
                  {!m.isCompatible && !m.isInstalled && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                      {t('wanVace.insufficientVram', 'Insufficient VRAM')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                  {t('wanVace.vram', 'VRAM')}: {m.vramRequired} GB
                  <span className="mx-2">|</span>
                  {t('wanVace.disk', 'Disk')}: {m.diskRequired} GB
                </div>
              </div>
              <div className="flex items-center gap-2">
                {m.isInstalled ? (
                  <>
                    {!m.isActive && (
                      <button
                        onClick={() => void selectModel(m.modelId)}
                        disabled={installing}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-secondary-700 dark:text-secondary-300 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors disabled:opacity-50"
                      >
                        {t('wanVace.select', 'Select')}
                      </button>
                    )}
                    <button
                      onClick={() => void uninstallModel(m.modelId)}
                      disabled={installing}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                    >
                      {t('wanVace.uninstall', 'Uninstall')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => void install(m.modelId)}
                    disabled={!m.isCompatible || installing}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('wanVace.install', 'Install')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No compatible hardware */}
      {!anyCompatible && status && !status.installed && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300">
          {t(
            'wanVace.noCompatibleGpu',
            'No compatible GPU found. Minimum 12GB VRAM required. You can still use Wan VACE via cloud.'
          )}
        </div>
      )}

      {/* LoRA section - only visible when installed */}
      {status?.installed && (
        <div className="pt-3 border-t border-secondary-200 dark:border-secondary-700">
          <div className="flex items-center justify-between mb-2">
            <h4 className="label-emphasized">
              {t('wanVace.loras.title', 'Speed LoRAs (optional)')}
            </h4>
            {lorasLoading && (
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                {t('wanVace.loras.loading', 'Loading...')}
              </span>
            )}
          </div>
          {lorasError && (
            <div className="mb-2 p-2 rounded text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
              {lorasError}
            </div>
          )}
          <div className="space-y-2">
            {loras.map((lora) => {
              const busy = loraActionId === lora.id;
              return (
                <div
                  key={lora.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 rounded border border-secondary-200 dark:border-secondary-700"
                >
                  <div>
                    <div className="label-emphasized">{lora.name}</div>
                    <div className="text-xs text-secondary-600 dark:text-secondary-400">
                      {t('wanVace.loras.download', 'Download')}: {lora.downloadGb.toFixed(1)} GB
                      <span className="mx-2">|</span>
                      {t('wanVace.loras.steps', 'Steps')}: {lora.recommendedSteps}
                      <span className="mx-2">|</span>
                      {lora.license}
                    </div>
                  </div>
                  {lora.installed ? (
                    <button
                      onClick={() => void handleLoraUninstall(lora.id)}
                      disabled={busy}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                    >
                      {busy
                        ? t('wanVace.loras.working', '...')
                        : t('wanVace.uninstall', 'Uninstall')}
                    </button>
                  ) : (
                    <button
                      onClick={() => void handleLoraInstall(lora.id)}
                      disabled={busy}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors disabled:opacity-50"
                    >
                      {busy ? t('wanVace.loras.working', '...') : t('wanVace.install', 'Install')}
                    </button>
                  )}
                </div>
              );
            })}
            {!lorasLoading && loras.length === 0 && !lorasError && (
              <div className="text-xs text-secondary-500 dark:text-secondary-400">
                {t('wanVace.loras.empty', 'No LoRAs available')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WanVaceCard;
